import {
  adminHostPendingInvoices,
  adminSearchInvoices,
  adminApproveInvoices,
  adminRemoveInvoice,
  adminPayInvoices,
  attachAdminInvoice,
  createAdminInvoice,
} from './adminInvoiceService'
import { uploadToS3 } from '@/services/upload/uploadService'
import { errorMessages } from '@/util/helpers'
import Vue from 'vue'

const state = {
  adminHostPendingInvoices: [],
  adminInvoices: [],
  adminInvoice: {},
  invoiceApprove: false,

  isAdminHostInvoiceLoading: false,
}

const getters = {
  adminHostPendingInvoices: (state) => state.adminHostPendingInvoices,
  adminInvoices: (state) => state.adminInvoices,
  adminInvoice: (state) => state.adminInvoice,
  invoiceApprove: (state) => state.invoiceApprove,

  isAdminHostInvoiceLoading: (state) => state.isAdminHostInvoiceLoading,
}

const actions = {
  async fetchAdminHostPendingInvoices({ commit, dispatch, rootState }) {
    commit('setIsLoading', true)

    const defaultParams = {
      size: rootState.paginate.itemsPerPage,
      page: rootState.paginate.currentPage - 1,
    }
    const params = { ...defaultParams, ...rootState.filters.filters }

    try {
      const { data } = await adminHostPendingInvoices(params)
      const { result, total } = await data

      commit('setAdminHostPendingInvoices', result)
      commit('setTotalPages', total)
    } catch ({ data }) {
      data?.errors && data?.errors?.length >= 1
        ? dispatch('showErrorMessage', errorMessages(data.errors))
        : dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsLoading', false)
    }
  },

  async fetchAdminInvoices({ commit, dispatch, rootState }) {
    try {
      commit('setIsLoading', true)
      commit('setItemsPerPage', 100)

      const defaultParams = {
        size: rootState.paginate.itemsPerPage,
        page: rootState.paginate.currentPage - 1,
      }
      const params = { ...defaultParams, ...rootState.filters.filters, ...{ sender: 'HOST' } }

      const { data } = await adminSearchInvoices(params)
      const { result, total } = await data

      commit('setAdminInvoices', result)
      commit('setTotalPages', total)
    } catch ({ data }) {
      data?.errors && data?.errors?.length >= 1
        ? dispatch('showErrorMessage', errorMessages(data.errors))
        : dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsLoading', false)
    }
  },

  async removeInvoices({ commit, dispatch }, invoiceId) {
    try {
      await adminRemoveInvoice(invoiceId)

      dispatch('showSuccessMessage', Vue.i18n.translate('hausefulInvoices.modals.remove.success'))
      dispatch('fetchAdminInvoices')
      commit('closeAllModals')
    } catch({ data }) {
      commit('closeAllModals')
      data?.errors && data?.errors?.length >= 1
      ? dispatch('showErrorMessage', errorMessages(data.errors))
      : dispatch('showErrorMessage', data.message)
    }
  },

  async approveInvoices({ commit, dispatch }, { invoiceId, approved, comment }) {
    try {
      await adminApproveInvoices({ invoiceId, approved, comment })

      if (approved) {
        dispatch('showSuccessMessage', Vue.i18n.translate('hausefulInvoices.modals.approve.success'))
      } else {
        dispatch('showSuccessMessage', Vue.i18n.translate('hausefulInvoices.modals.refuse.success'))
      }

      dispatch('fetchAdminInvoices')
      commit('closeAllModals')
    } catch ({ data }) {
      commit('closeAllModals')
      data?.errors && data?.errors?.length >= 1
        ? dispatch('showErrorMessage', errorMessages(data.errors))
        : dispatch('showErrorMessage', data.message)
    }
  },

async payInvoices({ commit, dispatch }, payloadOrIds) {
  try {
    const payload = Array.isArray(payloadOrIds)
      ? { invoice_ids: payloadOrIds }
      : payloadOrIds

    await adminPayInvoices(payload)

    dispatch('showSuccessMessage', Vue.i18n.translate('hausefulInvoices.modals.pay.success'))

    await Promise.all([
      dispatch('fetchAdminInvoices'),
      dispatch('fetchAdminHostPendingInvoices')
    ])

    commit('closeAllModals')
  } catch ({ data }) {
    commit('closeAllModals')
    data?.errors && data?.errors?.length >= 1
      ? dispatch('showErrorMessage', errorMessages(data.errors))
      : dispatch('showErrorMessage', data.message)
  }
},

  async createAdminHostInvoice({ commit, dispatch }, invoice) {
    commit('setIsAdminInvoiceLoading', true)

    if (invoice?.payment_ids?.length < 1) {
      commit('setIsAdminHostInvoiceLoading', false)
      dispatch('showErrorMessage', Vue.i18n.translate('hausefulInvoices.messages.noInvoicePayments'))
      return
    }

    try {
      const invoiceData = {
        payments_ids: invoice.payment_ids,
        signed_object_url: {
          filename: invoice.file.name,
          filetype: invoice.file.type,
          folder_name: 'host_invoices',
        }
      }

      await createAdminInvoice(invoiceData)
        .then(async ({ data }) => {
          try {
            await uploadToS3(data.signed_object_url.url, invoice.file)
            await attachAdminInvoice({
              id: data.invoice_response.id,
              filename: invoice.file.name,
              external_filename: data.signed_object_url.external_filename,
            })
          } catch ({ data }) {
            dispatch('showErrorMessage', data.message)
          }
        })
        .then(() => {
          commit('closeAllModals')
          dispatch('showSuccessMessage', Vue.i18n.translate('hausefulInvoices.modals.upload.success'))
          dispatch('fetchHostUserPendingInvoices')
        })
    } catch ({ data }) {
      dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsAdminHostInvoiceLoading', false)
    }
  }
}

const mutations = {
  setAdminHostPendingInvoices(state, adminHostPendingInvoices) {
    state.adminHostPendingInvoices = adminHostPendingInvoices
  },

  setAdminInvoices(state, adminInvoices) {
    state.adminInvoices = adminInvoices
  },

  setAdminInvoice(state, adminInvoice) {
    state.adminInvoice = adminInvoice
  },

  setApproveInvoice(state, status) {
    state.invoiceApprove = status
  },

  setIsAdminHostInvoiceLoading(state, status) {
    state.adminInvoiceLoading = status
  },
}

export default {
  state, mutations, actions, getters
}
